<template>
  <div class="order-detail-page">
    <!-- Redirect Overlay - Disables page during redirect -->
    <div v-if="redirecting" class="redirect-overlay">
      <div class="redirect-overlay-content">
        <div class="payment-spinner"></div>
        <h3>{{ redirectMessage }}</h3>
        <p>Please wait...</p>
      </div>
    </div>

    <!-- Payment Processing Overlay -->
    <div v-if="paymentProcessing && !redirecting" class="payment-overlay">
      <div class="payment-overlay-content">
        <div class="payment-spinner"></div>
        <h3>Processing Payment...</h3>
        <p>Please wait while we connect to the payment gateway.</p>
        <p class="payment-note">Do not close this page.</p>
        <button @click="cancelPaymentOverlay" class="btn-cancel-payment">
          <i class="fas fa-times"></i> Cancel
        </button>
      </div>
    </div>

    <div class="container">
      <div class="page-header">
        <a href="/dashboard/orders" class="btn-back">
          <i class="fas fa-arrow-left"></i> My Orders
        </a>
        <h1>Order #{{ order.number }}</h1>
        <span :class="['status-badge', 'status-' + order.status]">{{ formatStatus(order.status) }}</span>
      </div>

      <div class="order-grid">
        <!-- Order Details -->
        <div class="order-details-section">
          <!-- Payment Alert for Pending Orders or Rejected Payment -->
          <div v-if="showPaymentOptions" class="payment-alert">
            <i class="fas fa-exclamation-circle"></i>
            <div>
              <h3>{{ isPaymentRejected ? 'Payment Rejected' : 'Payment Required' }}</h3>
              <p v-if="isPaymentRejected">Your payment was rejected. Please try again with a different payment method.</p>
              <p v-else>Your order is awaiting payment. Please complete payment to process your order.</p>
            </div>
          </div>

          <!-- Order Items -->
          <div class="section-card">
            <h2><i class="fas fa-shopping-bag"></i> Order Items</h2>
            <div class="order-items">
              <div v-for="item in order.order_items" :key="item.id" class="order-item">
                <div class="item-image">
                  <img :src="item.product?.main_image || '/images/placeholder.jpg'" :alt="item.product_name">
                </div>
                <div class="item-details">
                  <h4>{{ item.product_name }}</h4>
                  <p class="item-variant" v-if="item.variant_id">Variant: {{ item.variant?.name || 'N/A' }}</p>
                  <p class="item-qty">Quantity: {{ item.quantity }}</p>
                </div>
                <div class="item-price">
                  <span class="price">₦{{ formatPrice(item.price * item.quantity) }}</span>
                  <small>₦{{ formatPrice(item.price) }} each</small>
                </div>
              </div>
            </div>
          </div>

          <!-- Shipping Information (hidden for pending orders) -->
          <div v-if="order.status !== 'pending' && order.shipping" class="section-card">
            <h2><i class="fas fa-truck"></i> Shipping Address</h2>
            <div class="shipping-info">
              <p v-if="order.shipping.recipient_first_name || order.shipping.recipient_last_name">{{ order.shipping.recipient_first_name }} {{ order.shipping.recipient_last_name }}</p>
              <p>{{ order.shipping.address }}</p>
              <p>{{ order.shipping.city }}<span v-if="order.shipping.state">, {{ order.shipping.state.name }}</span> {{ order.shipping.zip }}</p>
              <p v-if="order.shipping.recipient_phone">{{ order.shipping.recipient_phone }}</p>
              <p v-if="order.shipping.delivery_notes" class="delivery-notes">{{ order.shipping.delivery_notes }}</p>
            </div>
          </div>
        </div>

        <!-- Payment Section -->
        <div class="payment-section">
          <!-- Order Summary -->
          <div class="section-card summary-card">
            <h2>Order Summary</h2>
            <div class="summary-rows">
              <div class="summary-row">
                <span>Subtotal</span>
                <span>₦{{ formatPrice(order.subtotal) }}</span>
              </div>
              <div v-if="order.discount_amount > 0" class="summary-row discount">
                <span>Discount <small v-if="order.coupon_code">({{ order.coupon_code }})</small></span>
                <span>-₦{{ formatPrice(order.discount_amount) }}</span>
              </div>
              <div class="summary-row">
                <span>Shipping</span>
                <span>₦{{ formatPrice(order.shipping_cost) }}</span>
              </div>
              <div v-if="order.tax > 0" class="summary-row">
                <span>Tax</span>
                <span>₦{{ formatPrice(order.tax) }}</span>
              </div>
              <div class="summary-row total">
                <span>Total</span>
                <span>₦{{ formatPrice(order.total) }}</span>
              </div>
            </div>
          </div>

          <!-- Payment Options (for pending orders or rejected payments) -->
          <div v-if="showPaymentOptions" class="section-card payment-card">
            <h2><i class="fas fa-credit-card"></i> Complete Payment</h2>

            <div v-if="enabledPaymentMethods.length === 0" class="no-payment-methods">
              <p>No payment methods available. Please contact support.</p>
            </div>
            <div v-else class="payment-methods">
              <label
                v-for="method in enabledPaymentMethods"
                :key="method.code"
                class="payment-method"
                :class="{ active: selectedMethod === method.code }"
              >
                <input type="radio" v-model="selectedMethod" :value="method.code">
                <div class="method-content">
                  <i :class="getPaymentMethodIcon(method)"></i>
                  <div>
                    <h4>{{ method.name }}</h4>
                    <p>{{ method.description }}</p>
                  </div>
                </div>
              </label>
            </div>

            <!-- Bank Transfer Instructions -->
            <div v-if="selectedMethodDetails && selectedMethodDetails.type === 'manual'" class="bank-transfer-info">
              <h3><i class="fas fa-info-circle"></i> Bank Transfer Details</h3>
              <div class="bank-details">
                <div v-if="selectedMethodDetails.bank_name" class="detail-row">
                  <span>Bank:</span>
                  <strong>{{ selectedMethodDetails.bank_name }}</strong>
                </div>
                <div v-if="selectedMethodDetails.account_name" class="detail-row">
                  <span>Account Name:</span>
                  <strong>{{ selectedMethodDetails.account_name }}</strong>
                </div>
                <div v-if="selectedMethodDetails.account_number" class="detail-row">
                  <span>Account Number:</span>
                  <strong>{{ selectedMethodDetails.account_number }}</strong>
                  <button @click="copyAccountNumber" class="btn-copy" title="Copy">
                    <i class="fas fa-copy"></i>
                  </button>
                </div>
                <div class="detail-row amount">
                  <span>Amount:</span>
                  <strong>₦{{ formatPrice(order.total) }}</strong>
                </div>
                <div class="detail-row">
                  <span>Reference:</span>
                  <strong>{{ order.number }}</strong>
                </div>
              </div>
              <p v-if="selectedMethodDetails.instructions" class="instructions">
                {{ selectedMethodDetails.instructions }}
              </p>
            </div>

            <button @click="handlePayment" class="btn-pay" :disabled="!selectedMethod || loading">
              <i v-if="loading" class="fas fa-spinner fa-spin"></i>
              <i v-else class="fas fa-lock"></i>
              {{ loading ? 'Processing...' : `Pay ₦${formatPrice(order.total)}` }}
            </button>

            <p class="secure-notice">
              <i class="fas fa-shield-alt"></i>
              Secure payment powered by trusted gateways
            </p>
          </div>

          <!-- Payment Completed -->
          <div v-else class="section-card payment-status-card">
            <div v-if="isPaymentAwaitingConfirmation" class="payment-pending">
              <i class="fas fa-clock"></i>
              <h3>Payment Awaiting Confirmation</h3>
              <p>We've received your payment proof. Our team is verifying your payment.</p>
            </div>
            <div v-else-if="order.status === 'paid' || order.status === 'processing' || order.status === 'shipped' || order.status === 'delivered'" class="payment-success">
              <i class="fas fa-check-circle"></i>
              <h3>Payment Confirmed</h3>
              <p>Your payment has been received. Thank you for your order!</p>
            </div>
            <div v-else-if="order.status === 'cancelled'" class="payment-cancelled">
              <i class="fas fa-times-circle"></i>
              <h3>Order Cancelled</h3>
              <p>This order has been cancelled.</p>
            </div>
            <div v-else class="payment-info">
              <i class="fas fa-info-circle"></i>
              <h3>Order Status: {{ formatStatus(order.status) }}</h3>
            </div>
          </div>

          <!-- Order Timeline -->
          <div class="section-card timeline-card">
            <h2><i class="fas fa-history"></i> Order Timeline</h2>
            <div class="timeline">
              <div class="timeline-item" :class="{ active: true }">
                <div class="timeline-marker"></div>
                <div class="timeline-content">
                  <h4>Order Placed</h4>
                  <p>{{ formatDate(order.created_at) }}</p>
                </div>
              </div>
              <div class="timeline-item" :class="{ active: isPaymentConfirmed }">
                <div class="timeline-marker"></div>
                <div class="timeline-content">
                  <h4>Payment Confirmed</h4>
                  <p v-if="isPaymentConfirmed">Payment received</p>
                  <p v-else-if="isPaymentAwaitingConfirmation" class="pending">Awaiting confirmation</p>
                  <p v-else class="pending">Awaiting payment</p>
                </div>
              </div>
              <div class="timeline-item" :class="{ active: order.status === 'processing' || order.status === 'shipped' || order.status === 'delivered' }">
                <div class="timeline-marker"></div>
                <div class="timeline-content">
                  <h4>Processing</h4>
                  <p>Order being prepared</p>
                </div>
              </div>
              <div class="timeline-item" :class="{ active: order.status === 'shipped' || order.status === 'delivered' }">
                <div class="timeline-marker"></div>
                <div class="timeline-content">
                  <h4>Shipped</h4>
                  <p>On the way</p>
                </div>
              </div>
              <div class="timeline-item" :class="{ active: order.status === 'delivered' }">
                <div class="timeline-marker"></div>
                <div class="timeline-content">
                  <h4>Delivered</h4>
                  <p>Order completed</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

    </div>
  </div>
</template>

<script>
export default {
  name: 'OrderDetail',
  props: {
    order: {
      type: Object,
      required: true
    },
    paymentMethods: {
      type: Array,
      default: () => []
    },
    latestPayment: {
      type: Object,
      default: null
    }
  },
  data() {
    return {
      selectedMethod: this.paymentMethods.length > 0 ? this.paymentMethods[0].code : '',
      loading: false,
      paymentProcessing: false,
      redirecting: false,
      redirectMessage: 'Redirecting...',
      paystackPublicKey: null,
      flutterwavePublicKey: null
    }
  },
  computed: {
    enabledPaymentMethods() {
      // Filter to only show enabled payment methods (is_enabled = true or 1)
      return this.paymentMethods.filter(method => method.is_enabled === true || method.is_enabled === 1)
    },
    selectedMethodDetails() {
      return this.enabledPaymentMethods.find(m => m.code === this.selectedMethod)
    },
    isPaymentRejected() {
      // Check if latest payment was rejected
      return this.latestPayment?.verification_status === 'rejected'
    },
    showPaymentOptions() {
      // Show payment options if order is pending OR if awaiting_confirmation with rejected payment
      return this.order.status === 'pending' ||
        (this.order.status === 'awaiting_confirmation' && this.isPaymentRejected)
    },
    isPaymentAwaitingConfirmation() {
      // Check if latest payment is awaiting confirmation (not rejected)
      return this.order.status === 'awaiting_confirmation' &&
        this.latestPayment?.verification_status === 'awaiting_confirmation'
    },
    isPaymentConfirmed() {
      // Payment is confirmed if order status is not pending/cancelled AND not awaiting confirmation
      const validStatuses = ['paid', 'processing', 'shipped', 'delivered']
      return validStatuses.includes(this.order.status) && !this.isPaymentAwaitingConfirmation
    }
  },
  mounted() {
    // Get public keys from payment methods
    const paystackMethod = this.paymentMethods.find(m => m.code === 'paystack')
    this.paystackPublicKey = paystackMethod?.public_key || ''

    const flutterwaveMethod = this.paymentMethods.find(m => m.code === 'flutterwave')
    this.flutterwavePublicKey = flutterwaveMethod?.public_key || ''

    // Load Flutterwave script if needed
    this.loadFlutterwaveScript()
  },
  methods: {
    formatPrice(price) {
      return new Intl.NumberFormat('en-NG').format(price || 0)
    },

    formatStatus(status) {
      const statuses = {
        'pending': 'Pending Payment',
        'paid': 'Paid',
        'processing': 'Processing',
        'shipped': 'Shipped',
        'delivered': 'Delivered',
        'cancelled': 'Cancelled'
      }
      return statuses[status] || status
    },

    formatDate(dateString) {
      if (!dateString) return ''
      return new Date(dateString).toLocaleDateString('en-NG', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      })
    },

    getPaymentMethodIcon(method) {
      const icons = {
        'paystack': 'fas fa-credit-card',
        'flutterwave': 'fas fa-wallet',
        'bank_transfer': 'fas fa-university',
        'stripe': 'fab fa-stripe'
      }
      if (icons[method.code]) return icons[method.code]
      if (method.type === 'manual') return 'fas fa-money-bill-wave'
      return 'fas fa-credit-card'
    },

    copyAccountNumber() {
      const accountNumber = this.selectedMethodDetails?.account_number
      if (accountNumber) {
        navigator.clipboard.writeText(accountNumber)
        if (window.Swal) {
          window.Swal.fire({
            icon: 'success',
            title: 'Copied!',
            text: 'Account number copied to clipboard',
            timer: 1500,
            showConfirmButton: false,
            position: 'top-end',
            toast: true
          })
        }
      }
    },

    loadFlutterwaveScript() {
      if (document.getElementById('flutterwave-script')) return
      const script = document.createElement('script')
      script.id = 'flutterwave-script'
      script.src = 'https://checkout.flutterwave.com/v3.js'
      document.head.appendChild(script)
    },

    cancelPaymentOverlay() {
      this.paymentProcessing = false
      this.loading = false
    },

    async handlePayment() {
      if (!this.selectedMethod) return

      this.loading = true

      // For gateway payments, handle inline
      if (this.selectedMethod === 'paystack') {
        await this.initializePaystackPayment()
        return
      }

      if (this.selectedMethod === 'flutterwave') {
        this.initializeFlutterwavePayment()
        return
      }

      // For other methods (manual/bank transfer), use server
      try {
        const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content

        const response = await fetch(`/orders/${this.order.number}/pay`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRF-TOKEN': csrfToken,
            'Accept': 'application/json'
          },
          body: JSON.stringify({ payment_method: this.selectedMethod })
        })

        const data = await response.json()

        if (response.ok) {
          if (data.type === 'manual') {
            if (window.Swal) {
              window.Swal.fire({
                icon: 'success',
                title: 'Order Confirmed!',
                html: `<p>${data.message}</p>`,
                confirmButtonText: 'OK',
                confirmButtonColor: '#F55203'
              }).then(() => {
                window.location.href = `/orders/${this.order.number}/success`
              })
            }
          } else if (data.authorization_url || data.payment_url) {
            window.location.href = data.authorization_url || data.payment_url
          }
        } else {
          throw new Error(data.message || 'Payment failed')
        }
      } catch (error) {
        if (window.Swal) {
          window.Swal.fire({
            icon: 'error',
            title: 'Payment Error',
            text: error.message || 'An error occurred',
            confirmButtonColor: '#F55203'
          })
        }
      } finally {
        this.loading = false
      }
    },

    async ensurePaystackLoaded() {
      return new Promise((resolve, reject) => {
        if (window.PaystackPop) {
          resolve()
          return
        }

        let script = document.querySelector('script[src*="paystack"]')
        if (!script) {
          script = document.createElement('script')
          script.src = 'https://js.paystack.co/v1/inline.js'
          script.async = true
          document.head.appendChild(script)
        }

        script.onload = () => resolve()
        script.onerror = () => reject(new Error('Failed to load Paystack'))

        setTimeout(() => {
          if (!window.PaystackPop) reject(new Error('Paystack timeout'))
        }, 10000)
      })
    },

    async initializePaystackPayment() {
      if (!this.paystackPublicKey) {
        window.Swal?.fire({
          icon: 'error',
          title: 'Configuration Error',
          text: 'Paystack is not configured',
          confirmButtonColor: '#F55203'
        })
        this.loading = false
        return
      }

      try {
        this.paymentProcessing = true
        await this.ensurePaystackLoaded()

        const handler = window.PaystackPop.setup({
          key: this.paystackPublicKey,
          email: this.order.user?.email || '',
          amount: this.order.total * 100,
          currency: 'NGN',
          ref: this.order.number + '-' + Date.now(),
          metadata: {
            order_id: this.order.id,
            order_number: this.order.number
          },
          callback: (response) => {
            this.handlePaymentCallback(response)
          },
          onClose: () => {
            this.paymentProcessing = false
            this.loading = false
            window.Swal?.fire({
              icon: 'info',
              title: 'Payment Cancelled',
              text: 'You can try again anytime',
              confirmButtonColor: '#F55203'
            })
          }
        })

        handler.openIframe()
        this.paymentProcessing = false

      } catch (error) {
        this.paymentProcessing = false
        this.loading = false
        window.Swal?.fire({
          icon: 'error',
          title: 'Payment Error',
          text: error.message,
          confirmButtonColor: '#F55203'
        })
      }
    },

    initializeFlutterwavePayment() {
      if (!window.FlutterwaveCheckout) {
        window.Swal?.fire({
          icon: 'error',
          title: 'Payment Error',
          text: 'Flutterwave not loaded. Please refresh.',
          confirmButtonColor: '#F55203'
        })
        this.loading = false
        return
      }

      this.paymentProcessing = true

      window.FlutterwaveCheckout({
        public_key: this.flutterwavePublicKey,
        tx_ref: this.order.number + '-' + Date.now(),
        amount: this.order.total,
        currency: 'NGN',
        payment_options: 'card,banktransfer,ussd',
        customer: {
          email: this.order.user?.email || '',
          phone_number: this.order.shipping?.recipient_phone || '',
          name: this.order.shipping ? `${this.order.shipping.recipient_first_name || ''} ${this.order.shipping.recipient_last_name || ''}`.trim() : ''
        },
        customizations: {
          title: 'Order Payment',
          description: 'Payment for Order #' + this.order.number,
          logo: window.location.origin + '/logos/logodark.svg'
        },
        callback: (response) => {
          this.handleFlutterwaveCallback(response)
        },
        onclose: () => {
          this.paymentProcessing = false
          this.loading = false
        }
      })

      this.paymentProcessing = false
    },

    async handlePaymentCallback(response) {
      try {
        const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content
        const verifyResponse = await fetch('/payment/callback/paystack?reference=' + response.reference, {
          headers: { 'Accept': 'application/json', 'X-CSRF-TOKEN': csrfToken }
        })

        if (verifyResponse.ok || verifyResponse.redirected) {
          this.redirectMessage = 'Payment Successful! Redirecting...'
          this.redirecting = true
          window.location.href = `/orders/${this.order.number}/success`
        } else {
          throw new Error('Verification failed')
        }
      } catch (error) {
        window.Swal?.fire({
          icon: 'error',
          title: 'Verification Error',
          text: 'Contact support with order: ' + this.order.number,
          confirmButtonColor: '#F55203'
        })
      } finally {
        this.loading = false
      }
    },

    async handleFlutterwaveCallback(response) {
      if (response.status === 'successful') {
        try {
          const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content
          const verifyResponse = await fetch('/payment/callback/flutterwave?transaction_id=' + response.transaction_id, {
            headers: { 'Accept': 'application/json', 'X-CSRF-TOKEN': csrfToken }
          })

          if (verifyResponse.ok || verifyResponse.redirected) {
            this.redirectMessage = 'Payment Successful! Redirecting...'
            this.redirecting = true
            window.location.href = `/orders/${this.order.number}/success`
          }
        } catch (error) {
          this.redirectMessage = 'Verification Error'
          this.redirecting = true
          setTimeout(() => {
            this.redirecting = false
          }, 2000)
        }
      } else {
        window.Swal?.fire({
          icon: 'error',
          title: 'Payment Failed',
          text: 'Please try again',
          confirmButtonColor: '#F55203'
        })
      }
      this.loading = false
    }
  }
}
</script>

